import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { FileLink } from '@/components/portal/file-link';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import type { AttachmentRow } from '@/lib/portal';
import { useT } from '@/lib/i18n';

export const Route = createFileRoute('/settings/attachments')({ component: Attachments });

/** Attachments list (gear › Lists › Attachments; QBO Attachments): every attached file. */
function Attachments() {
  const t = useT();
  const orgId = useOrgId();
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['portal', orgId, 'attachments', 'all'],
    queryFn: () => api<AttachmentRow[]>(`/orgs/${orgId}/attachments/all`),
    enabled: Boolean(orgId),
  });
  const needle = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (a) =>
      !needle ||
      a.fileName.toLowerCase().includes(needle) ||
      a.entityLabel.toLowerCase().includes(needle),
  );
  return (
    <>
      <PageHeader
        title={t('settingsPages.general.attachments.title')}
        description={t('settingsPages.general.attachments.description')}
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : list.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : list.isError ? (
        <EmptyState
          title={t('settingsPages.general.attachments.loadError')}
          hint={list.error.message}
        />
      ) : !list.data.length ? (
        <EmptyState
          title={t('settingsPages.general.attachments.emptyTitle')}
          hint={t('settingsPages.general.attachments.emptyHint')}
        />
      ) : (
        <>
          <div className="relative mb-3 w-[320px]">
            <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              className="ps-8"
              placeholder={t('settingsPages.general.attachments.find')}
              aria-label={t('settingsPages.general.attachments.find')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settingsPages.general.attachments.colFile')}</TableHead>
                  <TableHead>{t('settingsPages.general.attachments.colAttachedTo')}</TableHead>
                  <TableHead className="w-[150px]">
                    {t('settingsPages.general.attachments.colUploadedBy')}
                  </TableHead>
                  <TableHead className="w-[150px]">
                    {t('settingsPages.general.attachments.colDate')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.attachmentId} data-testid="attachment-row">
                    <TableCell className="max-w-[360px]">
                      <FileLink file={a} />
                    </TableCell>
                    <TableCell>
                      {a.entityLink ? (
                        <Link className="text-primary hover:underline" to={a.entityLink}>
                          {a.entityLabel}
                        </Link>
                      ) : (
                        a.entityLabel
                      )}
                    </TableCell>
                    <TableCell>{a.uploadedBy}</TableCell>
                    <TableCell className="tnum">{formatDateTime(a.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </>
  );
}
